import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash, PencilSimple, Hammer, Truck, Fire, Wrench, Anchor, ArrowsClockwise, Copy, PlusCircle, Warning, ShieldWarning, FilePdf, Gear, ChartPieSlice, CalendarBlank } from "@phosphor-icons/react";
import { api, apiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import OperationsKpis from "@/pages/OperationsKpis";
import OperationsBudget from "@/pages/OperationsBudget";
import OperationsFrontPlan from "@/pages/OperationsFrontPlan";
import OperationsWeeklyPlan from "@/pages/OperationsWeeklyPlan";
import { useOpsProjects, getOpsProject } from "@/components/OpsProjectSelector";
import OperationsDashboardPbi from "@/pages/OperationsDashboardPbi";
import OperationsTimeStudy from "@/pages/OperationsTimeStudy";
import OperationsPeriodSummary from "@/pages/OperationsPeriodSummary";
import OperationsDelays from "@/pages/OperationsDelays";
import SectionHeader from "@/components/SectionHeader";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DateField } from "@/components/DateField";
import EquipmentPicker from "@/components/EquipmentPicker";
import { CLAUSES, CLAUSE_TONE } from "@/lib/clauses";

import { EQUIPMENT, SHIFT_LABEL, findEquipment } from "@/lib/operationsCatalog";
import { useFrontOptions } from "@/lib/useFrontOptions";
import { useDevSectionOptions } from "@/lib/useDevSections";
import ComboInput from "@/components/ComboInput";

/**
 * Iter125 — Reporte de MAQUINARIA por PERÍODO (landscape).
 * Pide `date_from`, `date_to`, tipo de equipo (opcional, default = el de la
 * pestaña activa) y tag específico (opcional). Genera PDF con resumen
 * ejecutivo + detalle diario + top operadores/áreas por tipo.
 */
function PeriodReportButton({ dateHint, equipment }) {
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const monthAgo = (() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  })();
  const [dateFrom, setDateFrom] = useState(monthAgo);
  const [dateTo, setDateTo] = useState(dateHint || today);
  const [eqType, setEqType] = useState(equipment?.v || "");
  const [eqCode, setEqCode] = useState("");
  const [scope, setScope] = useState("tab");  // "tab" | "all" | "custom"
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (dateHint) setDateTo(dateHint); }, [dateHint]);
  useEffect(() => { if (equipment?.v) setEqType(equipment.v); }, [equipment?.v]);

  const go = async () => {
    if (!dateFrom || !dateTo) { toast.error("Elige el rango de fechas"); return; }
    if (dateFrom > dateTo) { toast.error("«Desde» debe ser anterior a «Hasta»"); return; }
    setBusy(true);
    try {
      const p = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (scope === "tab" && eqType) p.set("equipment_type", eqType);
      if (scope === "custom" && eqType) p.set("equipment_type", eqType);
      if (scope === "custom" && eqCode.trim()) p.set("equipment_code", eqCode.trim());
      const seg = scope === "all" ? "" : `-${eqType || "all"}`;
      const codeSeg = scope === "custom" && eqCode ? `-${eqCode}` : "";
      await downloadFile(
        `/operations/equipment-report/pdf?${p.toString()}`,
        `reporte-maquinaria${seg}${codeSeg}-${dateFrom}-a-${dateTo}.pdf`,
      );
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}
              data-testid="ops-period-report-btn"
              className="border-emerald-700 text-emerald-800 hover:bg-emerald-50 gap-1.5">
        <CalendarBlank size={14} weight="bold" /> Reporte por período
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="ops-period-report-dialog">
          <DialogHeader>
            <DialogTitle>Reporte de Maquinaria — Período</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline-label">Desde</Label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                       data-testid="ops-period-from" />
              </div>
              <div>
                <Label className="overline-label">Hasta</Label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                       data-testid="ops-period-to" />
              </div>
            </div>

            <div>
              <Label className="overline-label">Alcance del reporte</Label>
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger data-testid="ops-period-scope"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="tab">Sólo {equipment?.l || "el equipo actual"}</SelectItem>
                  <SelectItem value="all">Todos los tipos de maquinaria</SelectItem>
                  <SelectItem value="custom">Personalizado (tipo + tag)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {scope === "custom" && (
              <div className="grid grid-cols-2 gap-3 border border-slate-200 rounded p-2 bg-slate-50">
                <div>
                  <Label className="overline-label">Tipo</Label>
                  <Select value={eqType} onValueChange={setEqType}>
                    <SelectTrigger data-testid="ops-period-custom-type"><SelectValue placeholder="Elegir…" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      <SelectItem value="">Todos</SelectItem>
                      {EQUIPMENT.map((eq) => (
                        <SelectItem key={eq.v} value={eq.v}>{eq.l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="overline-label">Tag específico</Label>
                  <Input value={eqCode} onChange={(e) => setEqCode(e.target.value)}
                         placeholder="ej. JMB-04" data-testid="ops-period-custom-code" />
                </div>
              </div>
            )}

            <p className="text-[11px] text-slate-500 italic">
              El PDF incluye: resumen ejecutivo por tipo, totales de producción
              y consumos, detalle diario y top operadores/áreas.
            </p>
          </div>
          <div className="flex gap-2 justify-end pt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={go} disabled={busy || !dateFrom || !dateTo}
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                    data-testid="ops-period-download">
              <FilePdf size={14} weight="bold" className="mr-1.5" />
              {busy ? "Generando…" : "Descargar PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Botón para descargar el Reporte Diario de Operación (PDF landscape).
 * Abre un mini-diálogo pidiendo fecha, turno y nombre del supervisor.
 * Genera el PDF con TODOS los equipos capturados en ese turno (no filtra
 * por equipment type de la pestaña actual — es un reporte consolidado).
 */
function DailyReportButton({ dateHint }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(dateHint || new Date().toISOString().slice(0, 10));
  const [shift, setShift] = useState("dia");
  const [supervisor, setSupervisor] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (dateHint) setDate(dateHint); }, [dateHint]);
  const go = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams({ date, shift, supervisor });
      await downloadFile(`/operations/daily-report/pdf?${params.toString()}`, `reporte-diario-${date}-${shift}.pdf`);
      setOpen(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="ops-daily-report-btn"
              className="border-blue-900 text-blue-900 hover:bg-blue-50 gap-1.5">
        <FilePdf size={14} weight="bold" /> Reporte diario PDF
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="ops-daily-report-dialog">
          <DialogHeader><DialogTitle>Reporte Diario de Operación</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="overline-label">Fecha</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="ops-daily-date" /></div>
            <div><Label className="overline-label">Turno</Label>
              <Select value={shift} onValueChange={setShift}>
                <SelectTrigger data-testid="ops-daily-shift"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="dia">Día</SelectItem>
                  <SelectItem value="noche">Noche</SelectItem>
                </SelectContent>
              </Select></div>
            <div className="col-span-2"><Label className="overline-label">Supervisor (firma)</Label>
              <Input value={supervisor} onChange={(e) => setSupervisor(e.target.value)} placeholder="Nombre del supervisor" data-testid="ops-daily-supervisor" /></div>
          </div>
          <div className="flex gap-2 justify-end pt-3">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={go} disabled={busy || !date} className="bg-blue-900 hover:bg-blue-800" data-testid="ops-daily-download">
              <FilePdf size={14} weight="bold" className="mr-1.5" /> {busy ? "Generando…" : "Descargar PDF"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Empty per-segment payload built from the equipment definition. */
const emptySegment = (eq) => {
  const m = {};
  if (eq.needsOrigin) { m.origen = ""; m.destino = ""; m.distancia_m = ""; }
  if (eq.needsFuel) m.diesel_lt = "";
  // Horómetros son un contador físico del equipo → viven en el header del turno,
  // no en cada registro/área. Se copian a cada segmento al guardar.
  if (eq.needsDelays) { m.demora_inicio = ""; m.demora_fin = ""; m.demora_motivo = ""; }
  if (eq.servicesCatalog) m.tipo = "";
  eq.metrics.forEach((f) => { m[f.k] = ""; });
  return { area: "", metrics: m };
};

const emptyHeader = (eq) => {
  const h = {
    date: new Date().toISOString().slice(0, 10),
    shift: "dia",
    project_id: getOpsProject(),
    equipment_code: "",
    operator_id: "", operator_name: "",
    helper_id: "", helper_name: "",
    hours: 0,
    notes: "",
  };
  if (eq?.needsHorometer) { h.horometro_inicial = ""; h.horometro_final = ""; }
  if (eq?.needsHorometerEP) {
    h.horometro_electrico_inicial = ""; h.horometro_electrico_final = "";
    h.horometro_percusion_inicial = ""; h.horometro_percusion_final = "";
  }
  return h;
};

/** Concise metric summary shown per row in the list. */
function metricSummary(equipment, entry) {
  const m = entry.metrics || {};
  const parts = [];
  if (m.tipo) parts.push(m.tipo);
  equipment.metrics.forEach((f) => {
    const v = m[f.k];
    if (v != null && v !== "") parts.push(`${f.l.split(" ")[0]}: ${v}`);
  });
  const extras = [];
  if (equipment.needsOrigin && m.origen) extras.push(`${m.origen}→${m.destino || "?"}`);
  if (equipment.needsFuel && m.diesel_lt) extras.push(`⛽ ${m.diesel_lt} lt`);
  if (equipment.needsHorometer && (m.horometro_final || m.horometro_inicial))
    extras.push(`⏱ ${m.horometro_inicial || "?"}→${m.horometro_final || "?"}`);
  if (equipment.needsHorometerEP && (m.horometro_electrico_inicial || m.horometro_electrico_final))
    extras.push(`⚡ ${m.horometro_electrico_inicial || "?"}→${m.horometro_electrico_final || "?"}`);
  if (equipment.needsDelays && (m.demora_inicio || m.demora_fin))
    extras.push(`⛔ ${m.demora_inicio || "?"}–${m.demora_fin || "?"}`);
  return [...parts, ...extras].join(" · ");
}

function SegmentCard({ eq, segment, idx, canRemove, onChange, onRemove, serviceTypes, onAddServiceType }) {
  const setMetric = (k, v) => onChange({ ...segment, metrics: { ...segment.metrics, [k]: v } });
  const setArea = (v) => onChange({ ...segment, area: v });
  const frontOptions = useFrontOptions();
  const sectionOptions = useDevSectionOptions();
  const num = (v) => (v === "" ? "" : Number(v));

  return (
    <div className={`border-2 border-dashed rounded-lg p-3 ${eq.color} space-y-2`} data-testid={`ops-seg-${idx}`}>
      <div className="flex items-center justify-between">
        <p className="overline-label text-slate-700">Área / registro #{idx + 1}</p>
        {canRemove && (
          <button onClick={onRemove} className="text-slate-400 hover:text-red-600" data-testid={`ops-seg-remove-${idx}`}>
            <Trash size={13} />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div><Label className="text-[10px] uppercase font-bold text-slate-500">Obra / zona</Label>
          <ComboInput value={segment.area} onChange={setArea} options={frontOptions}
                      placeholder="Elige obra del Plan o escribe" testid={`ops-seg-area-${idx}`} emptyText="Sin obras en Plan por Obra" /></div>
        {eq.servicesCatalog && (
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Tipo de servicio</Label>
            <div className="flex gap-1">
              <Select value={segment.metrics.tipo || ""} onValueChange={(v) => setMetric("tipo", v)}>
                <SelectTrigger data-testid={`ops-seg-tipo-${idx}`}><SelectValue placeholder="Elegir…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72 overflow-y-auto">
                  {serviceTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" className="px-2"
                      onClick={onAddServiceType} data-testid={`ops-seg-tipo-add-${idx}`}
                      title="Añadir nuevo tipo al catálogo">
                <PlusCircle size={14} />
              </Button>
            </div></div>
        )}
        {eq.needsOrigin && (
          <>
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Origen</Label>
              <Input value={segment.metrics.origen} onChange={(e) => setMetric("origen", e.target.value)} data-testid={`ops-seg-origen-${idx}`} /></div>
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Destino</Label>
              <Input value={segment.metrics.destino} onChange={(e) => setMetric("destino", e.target.value)} data-testid={`ops-seg-destino-${idx}`} /></div>
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Distancia (m)</Label>
              <Input type="number" step="any" value={segment.metrics.distancia_m}
                     onChange={(e) => setMetric("distancia_m", num(e.target.value))}
                     data-testid={`ops-seg-distancia-${idx}`} /></div>
          </>
        )}
        {eq.needsFuel && (
          <div><Label className="text-[10px] uppercase font-bold text-slate-500">Diésel (lt)</Label>
            <Input type="number" step="any" value={segment.metrics.diesel_lt}
                   onChange={(e) => setMetric("diesel_lt", num(e.target.value))}
                   data-testid={`ops-seg-diesel-${idx}`} /></div>
        )}
        {eq.needsDelays && (
          <>
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Demora · inicio</Label>
              <Input type="time" value={segment.metrics.demora_inicio}
                     onChange={(e) => setMetric("demora_inicio", e.target.value)}
                     data-testid={`ops-seg-di-${idx}`} /></div>
            <div><Label className="text-[10px] uppercase font-bold text-slate-500">Demora · fin</Label>
              <Input type="time" value={segment.metrics.demora_fin}
                     onChange={(e) => setMetric("demora_fin", e.target.value)}
                     data-testid={`ops-seg-df-${idx}`} /></div>
            <div className="col-span-2"><Label className="text-[10px] uppercase font-bold text-slate-500">Motivo de demora</Label>
              <Input value={segment.metrics.demora_motivo}
                     onChange={(e) => setMetric("demora_motivo", e.target.value)}
                     placeholder="ej. ventilación, cambio de acero…"
                     data-testid={`ops-seg-dm-${idx}`} /></div>
          </>
        )}
        {eq.metrics.map((f) => (
          <div key={f.k}>
            <Label className="text-[10px] uppercase font-bold text-slate-500">{f.l}</Label>
            {f.list === "dev-sections" ? (
              <ComboInput value={segment.metrics[f.k] ?? ""} onChange={(v) => setMetric(f.k, v)} options={sectionOptions}
                          placeholder="Elige la sección" testid={`ops-seg-${f.k}-${idx}`} emptyText="Sin secciones: créalas en Ajustes" />
            ) : (
            <Input type={f.type} step={f.type === "number" ? "any" : undefined}
                   value={segment.metrics[f.k] ?? ""}
                   onChange={(e) => setMetric(f.k, f.type === "number" ? num(e.target.value) : e.target.value)}
                   data-testid={`ops-seg-${f.k}-${idx}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function EquipmentTab({ equipment, canWrite, employees, refresh, serviceTypes, setServiceTypes }) {
  const [items, setItems] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [open, setOpen] = useState(false);
  const opsProjects = useOpsProjects();
  const [header, setHeader] = useState(emptyHeader());
  const [segments, setSegments] = useState([emptySegment(equipment)]);
  const [editing, setEditing] = useState(null);   // if set: single-row edit only
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const p = new URLSearchParams({ equipment_type: equipment.v });
    if (dateFilter) p.set("date", dateFilter);
    api.get(`/operations/entries?${p.toString()}`)
      .then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [equipment.v, dateFilter]);
  useEffect(() => { load(); }, [load, refresh]);

  const openNew = () => { setEditing(null); setHeader(emptyHeader(equipment)); setSegments([emptySegment(equipment)]); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    const hdr = {
      date: it.date, shift: it.shift, equipment_code: it.equipment_code || "",
      project_id: it.project_id || "",
      operator_id: it.operator_id || "", operator_name: it.operator_name || "",
      helper_id: it.helper_id || "", helper_name: it.helper_name || "",
      hours: it.hours || 0, notes: it.notes || "",
      penalizaciones: it.penalizaciones || {},
    };
    const src = it.metrics || {};
    if (equipment.needsHorometer) {
      hdr.horometro_inicial = src.horometro_inicial ?? "";
      hdr.horometro_final = src.horometro_final ?? "";
    }
    if (equipment.needsHorometerEP) {
      hdr.horometro_electrico_inicial = src.horometro_electrico_inicial ?? "";
      hdr.horometro_electrico_final = src.horometro_electrico_final ?? "";
      hdr.horometro_percusion_inicial = src.horometro_percusion_inicial ?? "";
      hdr.horometro_percusion_final = src.horometro_percusion_final ?? "";
    }
    setHeader(hdr);
    setSegments([{ area: it.area || "", metrics: { ...emptySegment(equipment).metrics, ...src } }]);
    setOpen(true);
  };
  const addSegment = () => setSegments((s) => [...s, emptySegment(equipment)]);
  const dupSegment = (i) => setSegments((s) => [...s, { ...s[i], metrics: { ...s[i].metrics } }]);
  const removeSegment = (i) => setSegments((s) => s.length > 1 ? s.filter((_, j) => j !== i) : s);
  const patchSegment = (i, next) => setSegments((s) => s.map((seg, j) => j === i ? next : seg));

  const promptAddServiceType = async () => {
    const label = window.prompt("Nuevo tipo de servicio:");
    if (!label) return;
    const next = Array.from(new Set([...(serviceTypes || []), label.trim()])).filter(Boolean);
    try {
      const r = await api.put("/settings/service-types", { values: next });
      setServiceTypes(r.data.values);
      toast.success(`Tipo "${label}" añadido al catálogo`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const save = async () => {
    if (opsProjects.length > 0 && !header.project_id) { toast.warning("Selecciona el proyecto del registro"); return; }
    setBusy(true);
    try {
      // Horómetros viven en el header del turno pero se persisten dentro de metrics
      // de cada registro para no romper compatibilidad con el resto del sistema
      // (reportes, bitácora, PDFs). Los aislamos aquí antes del payload.
      const sharedMetrics = {};
      if (equipment.needsHorometer) {
        sharedMetrics.horometro_inicial = header.horometro_inicial ?? "";
        sharedMetrics.horometro_final = header.horometro_final ?? "";
      }
      if (equipment.needsHorometerEP) {
        sharedMetrics.horometro_electrico_inicial = header.horometro_electrico_inicial ?? "";
        sharedMetrics.horometro_electrico_final = header.horometro_electrico_final ?? "";
        sharedMetrics.horometro_percusion_inicial = header.horometro_percusion_inicial ?? "";
        sharedMetrics.horometro_percusion_final = header.horometro_percusion_final ?? "";
      }
      const cleanHeader = { ...header };
      Object.keys(sharedMetrics).forEach((k) => delete cleanHeader[k]);

      if (editing) {
        const seg = segments[0];
        const payload = {
          ...cleanHeader, equipment_type: equipment.v,
          area: seg.area, hours: Number(header.hours || 0),
          metrics: { ...seg.metrics, ...sharedMetrics },
          penalizaciones: header.penalizaciones || {},
        };
        await api.put(`/operations/entries/${editing}`, payload);
        toast.success("Registro actualizado");
      } else {
        const entries = segments.map((seg) => ({
          ...cleanHeader, equipment_type: equipment.v,
          area: seg.area, hours: Number(header.hours || 0),
          metrics: { ...seg.metrics, ...sharedMetrics },
          penalizaciones: header.penalizaciones || {},
        }));
        const r = await api.post("/operations/entries/bulk", entries);
        toast.success(`Creados ${r.data.inserted} registro(s)`);
      }
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este registro?")) return;
    try { await api.delete(`/operations/entries/${id}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const Icon = equipment.icon;
  return (
    <div className="space-y-4" data-testid={`ops-tab-${equipment.v}`}>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="overline-label">Filtrar por fecha</Label>
          <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="h-9 w-40" data-testid={`ops-filter-date-${equipment.v}`} />
        </div>
        <span className="text-xs text-slate-500">{items.length} registro(s)</span>
        <div className="flex-1" />
        <DailyReportButton dateHint={dateFilter} equipment={equipment} />
        <PeriodReportButton dateHint={dateFilter} equipment={equipment} />
        {canWrite && (
          <Button className="bg-blue-900 hover:bg-blue-800 gap-1.5" onClick={openNew}
                  data-testid={`ops-new-${equipment.v}`}>
            <Plus size={14} weight="bold" /> Nuevo registro
          </Button>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider font-bold text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Fecha · Turno</th>
              <th className="text-left px-3 py-2">Obra · Equipo</th>
              <th className="text-left px-3 py-2">Operador · Ayudante</th>
              <th className="text-center px-3 py-2">Horas</th>
              <th className="text-left px-3 py-2">Detalle</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && (
              <tr><td colSpan={6} className="py-8 text-center text-slate-400">Sin registros para este equipo</td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} data-testid={`ops-row-${it.id}`}>
                <td className="px-3 py-2 whitespace-nowrap">
                  <p className="font-mono text-xs">{it.date}</p>
                  <span className={`inline-block text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${it.shift === "dia" ? "bg-amber-100 text-amber-800" : "bg-indigo-100 text-indigo-800"}`}>
                    {SHIFT_LABEL[it.shift] || it.shift}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <p className="font-semibold text-slate-800">{it.area || "-"}</p>
                  <p className="text-[10px] font-mono text-slate-500">{it.equipment_code || "-"}</p>
                </td>
                <td className="px-3 py-2">
                  <p className="font-medium">{it.operator_name || "-"}</p>
                  {it.helper_name && <p className="text-[10px] text-slate-500">Ayudante: {it.helper_name}</p>}
                </td>
                <td className="text-center">{it.hours || 0}</td>
                <td className="px-3 py-2 text-xs text-slate-600 max-w-md">
                  {metricSummary(equipment, it)}
                  {(() => {
                    const active = Object.entries(it.penalizaciones || {}).filter(([, v]) => v).map(([k]) => k);
                    if (!active.length) return null;
                    return (
                      <div className="mt-1 flex items-center gap-1 flex-wrap" data-testid={`ops-row-clauses-${it.id}`}>
                        <ShieldWarning size={11} weight="fill" className="text-rose-600" />
                        {active.map((k) => (
                          <span key={k} className="text-[9px] font-bold uppercase tracking-wider bg-rose-100 text-rose-700 px-1 py-0.5 rounded">
                            {(CLAUSES.find((c) => c.k === k)?.l || k).split(" ")[0]}
                          </span>
                        ))}
                        {it.audited_by && <span className="text-[9px] italic text-slate-400 ml-1">auditor: {it.audited_by}</span>}
                      </div>
                    );
                  })()}
                </td>
                <td className="text-right whitespace-nowrap">
                  {canWrite && (
                    <>
                      <button onClick={() => openEdit(it)} className="text-slate-400 hover:text-blue-900 mr-2"><PencilSimple size={14} /></button>
                      <button onClick={() => remove(it.id)} className="text-slate-400 hover:text-red-600 mr-2" data-testid={`ops-delete-${it.id}`}><Trash size={14} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Icon size={18} weight="bold" /> {editing ? "Editar" : "Nuevo"} · {equipment.label}
              {!editing && <span className="text-[11px] font-normal text-slate-500 ml-1">— multi-área permitida</span>}
            </DialogTitle>
          </DialogHeader>
          {/* ---------- Header ---------- */}
          <div className="grid grid-cols-2 gap-3 border-b border-slate-100 pb-3">
            <div><Label className="overline-label">Fecha</Label>
              <DateField testid={`ops-form-date-${equipment.v}`} value={header.date} onChange={(v) => setHeader({ ...header, date: v })} /></div>
            <div><Label className="overline-label">Turno</Label>
              <Select value={header.shift} onValueChange={(v) => setHeader({ ...header, shift: v })}>
                <SelectTrigger data-testid={`ops-form-shift-${equipment.v}`}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="dia">Día</SelectItem>
                  <SelectItem value="noche">Noche</SelectItem>
                </SelectContent>
              </Select></div>
            <div><Label className="overline-label">Depósito / Proyecto *</Label>
                <select value={header.project_id || ""} onChange={(e) => setHeader({ ...header, project_id: e.target.value })}
                        className={`w-full h-9 border rounded-md px-2 text-sm bg-white ${!header.project_id ? "border-amber-400" : ""}`} data-testid={`ops-form-project-${equipment.v}`}>
                  <option value="">— Elegir depósito / proyecto —</option>
                  {opsProjects.map((p) => <option key={p.id} value={p.id}>{p.code ? `${p.code} · ` : ""}{p.name}</option>)}
                </select>
                {opsProjects.length === 0 && <p className="text-[10px] text-slate-500 mt-0.5">Sin proyectos aún: créalos en Ajustes › Proyectos de Operaciones.</p>}
            </div>
            <div><Label className="overline-label">Código del equipo</Label>
              <EquipmentPicker
                mode="code"
                value={header.equipment_code}
                onChange={(v) => setHeader({ ...header, equipment_code: v })}
                dataTestId={`ops-form-code-${equipment.v}`}
                placeholder="Ej. JMB-04"
                allowInlineAdd={true}
                defaultType={equipment.v}
              /></div>
            <div><Label className="overline-label">Horas efectivas</Label>
              <Input type="number" step="0.1" value={header.hours} onChange={(e) => setHeader({ ...header, hours: e.target.value })} data-testid={`ops-form-hours-${equipment.v}`} /></div>

            {/* Horómetros: un solo valor por turno (contador físico del equipo) */}
            {equipment.needsHorometer && (
              <div className="col-span-2 grid grid-cols-2 gap-3 p-3 rounded-md bg-slate-50 border border-slate-200">
                <p className="col-span-2 overline-label text-blue-900 flex items-center gap-1.5">
                  ⏱ Horómetro del turno
                  <span className="text-[10px] normal-case tracking-normal font-normal text-slate-500">— aplica a todas las áreas capturadas</span>
                </p>
                <div><Label className="text-[10px] uppercase font-bold text-slate-500">Inicial</Label>
                  <Input type="number" step="any" value={header.horometro_inicial ?? ""}
                         onChange={(e) => setHeader({ ...header, horometro_inicial: e.target.value === "" ? "" : Number(e.target.value) })}
                         data-testid={`ops-form-hi-${equipment.v}`} /></div>
                <div><Label className="text-[10px] uppercase font-bold text-slate-500">Final</Label>
                  <Input type="number" step="any" value={header.horometro_final ?? ""}
                         onChange={(e) => setHeader({ ...header, horometro_final: e.target.value === "" ? "" : Number(e.target.value) })}
                         data-testid={`ops-form-hf-${equipment.v}`} /></div>
              </div>
            )}
            {equipment.needsHorometerEP && (
              <div className="col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3 p-3 rounded-md bg-slate-50 border border-slate-200">
                <p className="col-span-2 md:col-span-4 overline-label text-blue-900 flex items-center gap-1.5">
                  ⚡ Horómetros del turno · Eléctrico & Percusión
                  <span className="text-[10px] normal-case tracking-normal font-normal text-slate-500">— aplica a todas las áreas capturadas</span>
                </p>
                <div><Label className="text-[10px] uppercase font-bold text-slate-500">H. Eléc. inicial</Label>
                  <Input type="number" step="any" value={header.horometro_electrico_inicial ?? ""}
                         onChange={(e) => setHeader({ ...header, horometro_electrico_inicial: e.target.value === "" ? "" : Number(e.target.value) })}
                         data-testid={`ops-form-hei-${equipment.v}`} /></div>
                <div><Label className="text-[10px] uppercase font-bold text-slate-500">H. Eléc. final</Label>
                  <Input type="number" step="any" value={header.horometro_electrico_final ?? ""}
                         onChange={(e) => setHeader({ ...header, horometro_electrico_final: e.target.value === "" ? "" : Number(e.target.value) })}
                         data-testid={`ops-form-hef-${equipment.v}`} /></div>
                <div><Label className="text-[10px] uppercase font-bold text-slate-500">H. Perc. inicial</Label>
                  <Input type="number" step="any" value={header.horometro_percusion_inicial ?? ""}
                         onChange={(e) => setHeader({ ...header, horometro_percusion_inicial: e.target.value === "" ? "" : Number(e.target.value) })}
                         data-testid={`ops-form-hpi-${equipment.v}`} /></div>
                <div><Label className="text-[10px] uppercase font-bold text-slate-500">H. Perc. final</Label>
                  <Input type="number" step="any" value={header.horometro_percusion_final ?? ""}
                         onChange={(e) => setHeader({ ...header, horometro_percusion_final: e.target.value === "" ? "" : Number(e.target.value) })}
                         data-testid={`ops-form-hpf-${equipment.v}`} /></div>
              </div>
            )}

            <div className="col-span-2"><Label className="overline-label">Operador</Label>
              <Select value={header.operator_id || "__na__"} onValueChange={(v) => {
                const emp = employees.find((e) => e.id === v);
                setHeader({ ...header, operator_id: v === "__na__" ? "" : v, operator_name: emp?.name || "" });
              }}>
                <SelectTrigger data-testid={`ops-form-operator-${equipment.v}`}><SelectValue placeholder="Elegir colaborador…" /></SelectTrigger>
                <SelectContent className="bg-white max-h-72 overflow-y-auto">
                  <SelectItem value="__na__">Sin asignar</SelectItem>
                  {employees.filter((e) => e && e.id).map((e) => (
                    <SelectItem key={e.id} value={e.id}>{e.name || "(sin nombre)"} · {e.position || "-"}</SelectItem>
                  ))}
                </SelectContent>
              </Select></div>
            {equipment.needsHelper && (
              <div className="col-span-2"><Label className="overline-label">Ayudante (opcional)</Label>
                <Select value={header.helper_id || "__na__"} onValueChange={(v) => {
                  const emp = employees.find((e) => e.id === v);
                  setHeader({ ...header, helper_id: v === "__na__" ? "" : v, helper_name: emp?.name || "" });
                }}>
                  <SelectTrigger data-testid={`ops-form-helper-${equipment.v}`}><SelectValue placeholder="Elegir ayudante…" /></SelectTrigger>
                  <SelectContent className="bg-white max-h-72 overflow-y-auto">
                    <SelectItem value="__na__">Sin ayudante</SelectItem>
                    {employees.filter((e) => e && e.id).map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name || "(sin nombre)"} · {e.position || "-"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select></div>
            )}
            <div className="col-span-2"><Label className="overline-label">Observaciones</Label>
              <Textarea rows={2} value={header.notes} onChange={(e) => setHeader({ ...header, notes: e.target.value })} data-testid={`ops-form-notes-${equipment.v}`} /></div>
          </div>

          {/* ---------- Segments ---------- */}
          <div className="space-y-2">
            {segments.map((seg, i) => (
              <SegmentCard
                key={i}
                eq={equipment}
                segment={seg}
                idx={i}
                canRemove={!editing && segments.length > 1}
                onChange={(next) => patchSegment(i, next)}
                onRemove={() => removeSegment(i)}
                serviceTypes={serviceTypes}
                onAddServiceType={promptAddServiceType}
              />
            ))}
          </div>

          {!editing && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={addSegment} className="gap-1.5" data-testid={`ops-add-segment-${equipment.v}`}>
                <Plus size={13} /> Añadir otra área
              </Button>
              {segments.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => dupSegment(segments.length - 1)} className="gap-1.5"
                        data-testid={`ops-dup-segment-${equipment.v}`}>
                  <Copy size={13} /> Duplicar la última
                </Button>
              )}
            </div>
          )}

          {/* ---------- Cláusulas de Riesgo (auditor) ---------- */}
          {canWrite && (
            <div className="border border-rose-200 bg-rose-50/40 rounded-md p-3 space-y-2" data-testid={`ops-clauses-${equipment.v}`}>
              <div className="flex items-center justify-between">
                <p className="overline-label text-rose-700 flex items-center gap-1.5">
                  <Warning size={13} weight="fill" /> Cláusulas de riesgo (auditor)
                </p>
                <span className="text-[10px] normal-case tracking-normal font-normal text-slate-500">
                  Marca las que ocurrieron en este turno · aplica a todas las áreas
                </span>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                {CLAUSES.map((c) => {
                  const on = !!header.penalizaciones?.[c.k];
                  const tone = CLAUSE_TONE[c.severity];
                  return (
                    <label key={c.k}
                      className={`flex items-start gap-1.5 cursor-pointer rounded p-1.5 border border-slate-200 text-[11px] ${on ? `ring-2 ${tone}` : "bg-white hover:border-rose-300"}`}
                      data-testid={`ops-clause-${equipment.v}-${c.k}`}>
                      <input type="checkbox" checked={on}
                        onChange={() => setHeader({ ...header, penalizaciones: { ...(header.penalizaciones || {}), [c.k]: !on } })}
                        className="mt-0.5 accent-rose-500" />
                      <span className={on ? "font-semibold text-slate-900" : "text-slate-700"}>{c.l}</span>
                    </label>
                  );
                })}
              </div>
              {Object.values(header.penalizaciones || {}).some(Boolean) && (
                <p className="text-[10px] italic text-rose-700 pt-1 border-t border-rose-100">
                  ⚠ Estas cláusulas se registrarán con tu nombre y hora como sello de auditoría, y se descontarán automáticamente en Finanzas.
                </p>
              )}
            </div>
          )}

          <Button onClick={save} disabled={busy || !header.date} className="bg-blue-900 hover:bg-blue-800 w-full mt-2" data-testid={`ops-form-save-${equipment.v}`}>
            {busy ? "Guardando…" : editing ? "Actualizar registro"
              : (segments.length > 1 ? `Crear ${segments.length} registros` : "Crear registro")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Operations() {
  const { user } = useAuth();
  const { has } = usePermissions();
  const [employees, setEmployees] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api.get("/hr/employees?status=active").then((r) => setEmployees(r.data)).catch(() => setEmployees([]));
    api.get("/settings/service-types").then((r) => setServiceTypes(r.data.values || [])).catch(() => setServiceTypes([]));
  }, []);

  const visibleTabs = useMemo(
    () => EQUIPMENT.filter((e) => has(`operations.tabs.${e.v}`)),
    [has],
  );

  return (
    <div className="space-y-6">
      <SectionHeader
        icon={Hammer}
        title="Operaciones"
        eyebrow="Registro por turno · Multi-área por operador"
        size="lg"
        testid="ops-header"
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setRefresh((n) => n + 1)} data-testid="ops-refresh">
              <ArrowsClockwise size={14} weight="bold" /> Actualizar
            </Button>
          </div>
        }
      />

      {visibleTabs.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-16">Sin permisos para ver ningún sub-módulo de Operaciones.</p>
      ) : (
        <Tabs defaultValue="__equipos">
          <TabsList className="bg-slate-100 flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="__equipos" data-testid="ops-tab-trigger-equipos"
                         className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Gear size={13} weight="bold" className="mr-1.5" /> Equipos
            </TabsTrigger>
            <TabsTrigger value="__dashboard" data-testid="ops-tab-trigger-dashboard"
                         className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm border-l border-slate-300 ml-1 pl-2">
              <ChartPieSlice size={13} weight="bold" className="mr-1.5" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="__kpis" data-testid="ops-tab-trigger-kpis"
                         className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
              📊 KPIs
            </TabsTrigger>
            <TabsTrigger value="__budget" data-testid="ops-tab-trigger-budget"
                         className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
              🎯 Budget
            </TabsTrigger>
            <TabsTrigger value="__frontplan" data-testid="ops-tab-trigger-frontplan"
                         className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
              🏗️ Plan por Obra
            </TabsTrigger>
            <TabsTrigger value="__periodo" data-testid="ops-tab-trigger-periodo"
                         className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
              📋 Resumen Periodo
            </TabsTrigger>
            <TabsTrigger value="__timestudy" data-testid="ops-tab-trigger-timestudy"
                         className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
              ⏱️ Análisis de Tiempos
            </TabsTrigger>
            <TabsTrigger value="__delays" data-testid="ops-tab-trigger-delays"
                         className="text-xs data-[state=active]:bg-white data-[state=active]:shadow-sm">
              ⏸ Demoras
            </TabsTrigger>
          </TabsList>
          <TabsContent value="__delays" className="mt-4">
            <OperationsDelays />
          </TabsContent>
          <TabsContent value="__periodo" className="mt-4">
            <OperationsPeriodSummary />
          </TabsContent>
          <TabsContent value="__timestudy" className="mt-4">
            <OperationsTimeStudy canWrite={has("operations.write")} />
          </TabsContent>
          <TabsContent value="__equipos" className="mt-4">
            <UnifiedEquiposPanel visibleTabs={visibleTabs} canWrite={has("operations.write")}
                                  employees={employees} refresh={refresh}
                                  serviceTypes={serviceTypes} setServiceTypes={setServiceTypes} />
          </TabsContent>
          <TabsContent value="__dashboard" className="mt-4">
            <OperationsDashboardPbi />
          </TabsContent>
          <TabsContent value="__kpis" className="mt-4">
            <OperationsKpis />
          </TabsContent>
          <TabsContent value="__budget" className="mt-4">
            <OperationsBudget canWrite={has("operations.write")} />
          </TabsContent>
          <TabsContent value="__frontplan" className="mt-4">
            <Tabs defaultValue="monthly" data-testid="ops-plan-mode-tabs">
              <TabsList className="bg-slate-100 h-auto p-1">
                <TabsTrigger value="monthly" className="text-xs" data-testid="ops-plan-tab-monthly">📅 Plan Mensual</TabsTrigger>
                <TabsTrigger value="weekly" className="text-xs" data-testid="ops-plan-tab-weekly">🗓 Plan Semanal</TabsTrigger>
              </TabsList>
              <TabsContent value="monthly" className="mt-3"><OperationsFrontPlan canWrite={has("operations.write")} /></TabsContent>
              <TabsContent value="weekly" className="mt-3"><OperationsWeeklyPlan canWrite={has("operations.write")} /></TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

/**
 * UnifiedEquiposPanel — reemplaza los 9 tabs de equipos por un solo panel
 * con Select interno para elegir el tipo (Anclador, Jumbo, Voladuras,
 * Scoop, Camión, Servicios, Telehandler, Retro, Motoniv). Se persiste la
 * última selección en localStorage para que el usuario no la pierda.
 */
function UnifiedEquiposPanel({ visibleTabs, canWrite, employees, refresh, serviceTypes, setServiceTypes }) {
  const [selVal, setSelVal] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem("ops.equipos.sel") : null;
    if (saved && visibleTabs.some((t) => t.v === saved)) return saved;
    return visibleTabs[0]?.v || "";
  });
  useEffect(() => {
    if (!visibleTabs.some((t) => t.v === selVal) && visibleTabs.length) setSelVal(visibleTabs[0].v);
  }, [visibleTabs, selVal]);
  useEffect(() => {
    if (selVal) localStorage.setItem("ops.equipos.sel", selVal);
  }, [selVal]);
  const current = visibleTabs.find((t) => t.v === selVal) || visibleTabs[0];
  if (!current) return null;
  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-center gap-3">
        <div className="h-9 w-9 rounded-md bg-amber-100 flex items-center justify-center">
          <current.icon size={18} weight="duotone" className="text-amber-700" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">Tipo de equipo</p>
          <p className="font-heading font-black text-lg text-slate-900 leading-tight">{current.label}</p>
        </div>
        <div className="flex-1" />
        <div className="min-w-[240px]">
          <Label className="overline-label">Cambiar equipo</Label>
          <Select value={selVal} onValueChange={setSelVal}>
            <SelectTrigger data-testid="ops-equipos-selector" className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {visibleTabs.map((eq) => (
                <SelectItem key={eq.v} value={eq.v}>
                  <span className="inline-flex items-center gap-2">
                    <eq.icon size={13} weight="bold" /> {eq.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {/* Chips de acceso rápido — visibles en desktop */}
        <div className="w-full flex flex-wrap gap-1 pt-2 border-t border-slate-100 md:pt-0 md:border-0 md:w-auto md:pl-3 md:border-l">
          {visibleTabs.map((eq) => (
            <button key={eq.v} onClick={() => setSelVal(eq.v)}
                    data-testid={`ops-equipos-chip-${eq.v}`}
                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-1 rounded border transition-colors ${
                      eq.v === selVal
                        ? "bg-amber-500 text-white border-amber-500"
                        : "bg-white text-slate-600 border-slate-200 hover:border-amber-300 hover:text-amber-800"
                    }`}>
              <eq.icon size={10} weight="bold" /> {eq.label}
            </button>
          ))}
        </div>
      </div>
      <EquipmentTab equipment={current} canWrite={canWrite} employees={employees}
                    refresh={refresh} serviceTypes={serviceTypes} setServiceTypes={setServiceTypes} />
    </div>
  );
}
